/**
 * Whole-mission flight paths (pure, incremental). Each mission aircraft's observations since the
 * mission started are decimated to a thin polyline that stays cheap on long missions:
 *  - a point is kept when it is ≥ `stepM` from the last kept point (the newest point always is);
 *  - when a path grows past `maxPoints`, every other point is dropped and the step doubles.
 * Live records only ever append, so each update folds in just the new samples; anything else
 * (a capped head, a reloaded record) rebuilds from scratch. Replay cuts the path at the playhead.
 */
import type { EpochMs } from '@apos/shared';

export interface PathSample {
  t: EpochMs;
  lat: number;
  lon: number;
}

export interface DecimatedPath {
  /** Times of the kept points (ascending). */
  t: EpochMs[];
  /** [lon, lat] of the kept points. */
  coords: Array<[number, number]>;
  stepM: number;
}

export const PATH_STEP_M = 30;
export const PATH_MAX_POINTS = 1500;

const D2R = Math.PI / 180;
/** Equirectangular distance — plenty for a decimation threshold of tens of metres. */
function approxDistM(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const x = (bLon - aLon) * D2R * Math.cos(((aLat + bLat) / 2) * D2R);
  const y = (bLat - aLat) * D2R;
  return Math.hypot(x, y) * 6_371_008.8;
}

function halve(p: DecimatedPath): void {
  const n = p.t.length;
  const t: EpochMs[] = [];
  const c: Array<[number, number]> = [];
  for (let i = 0; i < n; i += 2) {
    t.push(p.t[i]!);
    c.push(p.coords[i]!);
  }
  if (t[t.length - 1] !== p.t[n - 1]) {
    t.push(p.t[n - 1]!);
    c.push(p.coords[n - 1]!);
  }
  p.t = t;
  p.coords = c;
  p.stepM *= 2;
}

/**
 * Fold samples (ascending t) into `p`. The newest sample is always the tail; when it is closer
 * than `stepM` to the last committed point it is provisional and replaced by the next sample.
 */
function append(p: DecimatedPath, samples: readonly PathSample[], from: number, maxPoints: number, provisional: { v: boolean }): void {
  for (let i = from; i < samples.length; i++) {
    const s = samples[i]!;
    if (p.t.length > 0 && s.t <= p.t[p.t.length - 1]!) continue;
    if (provisional.v) {
      p.t.pop();
      p.coords.pop();
    }
    const n = p.t.length;
    const near = n > 0 && approxDistM(p.coords[n - 1]![1], p.coords[n - 1]![0], s.lat, s.lon) < p.stepM;
    p.t.push(s.t);
    p.coords.push([s.lon, s.lat]);
    provisional.v = near;
    if (!near && p.t.length > maxPoints) halve(p);
  }
}

/** Decimate a whole sample list (ascending t). */
export function decimatePath(samples: readonly PathSample[], opts: { stepM?: number; maxPoints?: number; since?: EpochMs | null } = {}): DecimatedPath {
  const p: DecimatedPath = { t: [], coords: [], stepM: opts.stepM ?? PATH_STEP_M };
  const from = opts.since ? lowerBound(samples, opts.since) : 0;
  append(p, samples, from, opts.maxPoints ?? PATH_MAX_POINTS, { v: false });
  return p;
}

/** Kept points this far apart in time are different stretches of flying, not one continuous path. */
export const PATH_GAP_MS = 10 * 60_000;

/** The first `n` kept points as separate lines, cut wherever the feed went quiet for longer than `gapMs`. */
export function splitAtGaps(times: readonly EpochMs[], coords: ReadonlyArray<[number, number]>, n: number, gapMs = PATH_GAP_MS): Array<Array<[number, number]>> {
  const lines: Array<Array<[number, number]>> = [];
  let cur: Array<[number, number]> = [];
  for (let i = 0; i < n; i++) {
    if (i > 0 && times[i]! - times[i - 1]! > gapMs) {
      if (cur.length > 1) lines.push(cur);
      cur = [];
    }
    cur.push(coords[i]!);
  }
  if (cur.length > 1) lines.push(cur);
  return lines;
}

/** First index with t ≥ `t`. */
export function lowerBound(arr: readonly { t: EpochMs }[], t: EpochMs): number {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid]!.t < t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Number of kept points with t ≤ `t` (binary search on the ascending times). */
export function countUpTo(times: readonly EpochMs[], t: EpochMs): number {
  let lo = 0;
  let hi = times.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (times[mid]! <= t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

interface CacheEntry {
  src: readonly PathSample[];
  firstT: EpochMs;
  processed: number;
  since: EpochMs | null;
  path: DecimatedPath;
  provisional: { v: boolean };
}

/**
 * Per-aircraft incremental cache. `update(id, samples)` returns the decimated path, reusing the
 * previous work when `samples` is the previous array plus appended samples.
 */
export class FlightPathCache {
  private entries = new Map<string, CacheEntry>();
  constructor(private readonly opts: { stepM?: number; maxPoints?: number } = {}) {}

  update(id: string, samples: readonly PathSample[], since: EpochMs | null): DecimatedPath {
    const e = this.entries.get(id);
    const maxPoints = this.opts.maxPoints ?? PATH_MAX_POINTS;
    if (e && e.src === samples && e.since === since) return e.path;
    const n = samples.length;
    const appendable =
      e &&
      e.since === since &&
      n >= e.processed &&
      e.processed > 0 &&
      samples[0]?.t === e.firstT &&
      samples[e.processed - 1]?.t === e.src[e.processed - 1]?.t;
    if (e && appendable) {
      append(e.path, samples, e.processed, maxPoints, e.provisional);
      e.src = samples;
      e.processed = n;
      return e.path;
    }
    const provisional = { v: false };
    const path: DecimatedPath = { t: [], coords: [], stepM: this.opts.stepM ?? PATH_STEP_M };
    append(path, samples, since ? lowerBound(samples, since) : 0, maxPoints, provisional);
    this.entries.set(id, { src: samples, firstT: samples[0]?.t ?? 0, processed: n, since, path, provisional });
    return path;
  }

  /** Forget aircraft that are no longer on the mission. */
  retain(ids: ReadonlySet<string>): void {
    for (const id of [...this.entries.keys()]) if (!ids.has(id)) this.entries.delete(id);
  }

  clear(): void {
    this.entries.clear();
  }
}
